import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ChevronUp, Clock, Download, Link2, Loader2, Paperclip, ShieldCheck } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { downloadBlob } from "../lib/downloadBlob";
import { asciiName, versionLine } from "../lib/portalPresence";
import { longDate, whenLabel } from "../lib/portalPortfolio";
import {
  ROUND_LABELS, SHEET_STATES, levelOf, pointProblem, roundWhen, sheetHeadline, totalOf,
} from "../lib/portalComposition";
import { Chip, Shell, fieldCls, primaryBtn } from "../components/portal/PortalShell";

const LEVEL_TONE = { very_good: "good", good: "info", low: "warn" };

/**
 * `/portal/evaluare/:groupId` — an evaluator's sheet for the composition of the
 * portfolios (procedure 29.611, art. 28): the portfolios admitted at the
 * structure check, the grid of seven criteria with the level each score
 * reaches, and the sheet signed at the end (Anexa 8). The server checks every
 * bound again; this only says it sooner.
 *
 * Since PR 11a, a second tab: the final evaluation in the committee's
 * plenary (procedure, art. 30) — the same grid, on the presentation, for
 * whoever is present in Anexa 10; its own sheet, signed (Anexa 11).
 */
export default function PortalEvaluation() {
  const { groupId } = useParams();
  const [kind, setKind] = useState("main");
  const [round, setRound] = useState("composition");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/portal/groups/${groupId}/evaluation`, { params: { session: kind, round } })).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am găsit evaluarea."));
    }
  }, [groupId, kind, round]);
  const L = ROUND_LABELS[round];
  useEffect(() => { load(); }, [load]);

  return (
    <Shell wide>
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la portal
      </Link>
      {error && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!data && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {data && (
        <div className="mt-6 grid gap-8" data-testid="portal-evaluation">
          <header>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
              {round === "final" ? "Evaluarea finală" : "Evaluarea compoziției"} · {data.group.name}
            </p>
            <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#373A45]">Fișa ta de evaluare</h1>
            {(data.final_open || round === "final") && (
              <div className="mt-3 inline-flex overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="tablist"
                aria-label="Etapa evaluării">
                {Object.entries(ROUND_LABELS).map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={round === k}
                    onClick={() => { setRound(k); setOpen(null); }} data-testid={`ev-round-${k}`}
                    className={`px-4 py-1.5 text-sm font-semibold ${round === k ? "bg-[#965EA7] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}>
                    {l.tab}
                  </button>
                ))}
              </div>
            )}
            {data.has_resit && (
              <div className="mt-3 inline-flex overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="tablist">
                {[["main", "Sesiunea principală"], ["resit", "Sesiunea de restanță"]].map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={kind === k}
                    onClick={() => { setKind(k); setOpen(null); }}
                    className={`px-4 py-1.5 text-sm font-semibold ${kind === k ? "bg-[#373A45] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {data.session && (
              <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#373A45]">
                <Clock size={15} className="text-[#965EA7]" />
                {roundWhen(data, longDate)}
                <span className="text-xs text-[#6B7280]">({L.rule})</span>
              </p>
            )}
            <p className={`mt-3 rounded-xl px-4 py-3 text-sm ${data.sheet.status === "signed" ? "bg-[#519872]/10 text-[#3c7457]" : "bg-white text-[#373A45] ring-1 ring-[#E5E7EB]"}`}
              data-testid="portal-evaluation-headline">
              {sheetHeadline(data)}
            </p>
          </header>

          {data.rows.length > 0 && (
            <section aria-labelledby="ev-rows">
              <h2 id="ev-rows" className="font-display text-xl font-bold text-[#373A45]">{L.title}</h2>
              <p className="mt-1 text-sm text-[#6B7280]">
                {round === "composition"
                  ? `Fiecare portofoliu se punctează individual, de la 0 la 100, pe cele șapte criterii din grila programului. Strategia alocă cel puțin ${data.min_minutes} minute pentru un portofoliu.`
                  : "Fiecare cursant își susține portofoliul în plenul comisiei; îl punctezi individual, de la 0 la 100, pe aceeași grilă."}
                {" "}Punctajele celorlalți evaluatori nu se văd până nu semnează toți.
              </p>
              <ul className="mt-3 grid gap-3">
                {data.rows.map((r) => (
                  <li key={r.enrollment_id} className="rounded-2xl border border-[#E5E7EB] bg-white">
                    <button type="button" onClick={() => setOpen(open === r.enrollment_id ? null : r.enrollment_id)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left" aria-expanded={open === r.enrollment_id}
                      data-testid={`ev-open-${r.enrollment_id}`}>
                      <span className="min-w-0 flex-1 font-semibold text-[#373A45]">{r.name}</span>
                      {r.mean_shown && (
                        <span className="text-xs text-[#6B7280]">media {r.mean_shown} · {r.verdict}</span>
                      )}
                      {r.score
                        ? <Chip tone="good">{r.score.total_shown} puncte</Chip>
                        : <Chip tone={r.opened ? "info" : "neutral"}>{r.opened ? "Deschis" : "Nepunctat"}</Chip>}
                      {open === r.enrollment_id ? <ChevronUp size={16} className="text-[#9CA3AF]" /> : <ChevronDown size={16} className="text-[#9CA3AF]" />}
                    </button>
                    {open === r.enrollment_id && (
                      <div className="border-t border-[#E5E7EB] bg-[#FAFAFB] px-4 py-4">
                        <Portfolio groupId={groupId} kind={kind} round={round} eid={r.enrollment_id} data={data} onSaved={load} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.rows.length > 0 && <SignSheet groupId={groupId} kind={kind} round={round} data={data} onChange={setData} />}
          <Progress data={data} />
        </div>
      )}
    </Shell>
  );
}

function Portfolio({ groupId, kind, round, eid, data, onSaved }) {
  const [p, setP] = useState(null);
  const [points, setPoints] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const signed = data.sheet.status === "signed";

  useEffect(() => {
    api.get(`/portal/groups/${groupId}/evaluation/${eid}`, { params: { session: kind, round } }).then((r) => {
      setP(r.data);
      setPoints(r.data.score?.criteria || {});
      setNote(r.data.score?.note || "");
    }).catch((err) => toast.error(apiErrorMessage(err, "Nu am putut deschide portofoliul.")));
  }, [groupId, kind, round, eid]);

  if (!p) return <Loader2 className="animate-spin text-[#6B7280]" />;
  const total = totalOf(points, data.criteria);
  const problems = data.criteria.map((c) => pointProblem(points[String(c.n)], c.max));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/portal/groups/${groupId}/evaluation/${eid}`, { criteria: points, note },
        { params: { session: kind, round } });
      setP({ ...p, score: r.data.score });
      toast.success(`Am salvat punctajul: ${total}.`);
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva punctajul."));
    } finally {
      setBusy(false);
    }
  };
  const file = async (fid) => {
    try {
      window.location.assign((await api.get(`/portal/groups/${groupId}/evaluation/${eid}/files/${fid}`,
        { params: { session: kind, round } })).data.url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut descărca fișierul."));
    }
  };

  return (
    <div className="grid gap-5 text-sm text-[#373A45]" data-testid="ev-portfolio">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Ce s-a depus</p>
        <ul className="mt-2 grid gap-2">
          {p.slots.map((slot) => {
            const got = p.seal.slots?.[slot.key] || { files: [], links: [] };
            if (!got.files.length && !got.links.length && !slot.required) return null;
            return (
              <li key={slot.key} className="rounded-xl bg-white p-3 ring-1 ring-[#E5E7EB]">
                <p className="font-semibold">{slot.label}</p>
                {!got.files.length && !got.links.length && <p className="text-xs text-[#6B7280]">gol</p>}
                {got.files.map((f) => (
                  <button key={f.file_id} type="button" onClick={() => file(f.file_id)}
                    className="mt-1 flex items-center gap-1.5 text-left hover:text-[#965EA7]">
                    <Paperclip size={13} /> {f.name}
                  </button>
                ))}
                {got.links.map((ln, i) => (
                  <a key={i} href={ln.url} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1.5 text-[#965EA7] hover:underline">
                    <Link2 size={13} /> {ln.label || ln.url}
                  </a>
                ))}
              </li>
            );
          })}
        </ul>
        <p className="mt-2"><strong>Instrumentele AI:</strong> {p.seal.ai_tools || "—"}</p>
        <p className="mt-1 text-xs text-[#6B7280]">
          Depus și semnat de {p.seal.signature.full_name} la {whenLabel(p.seal.at)} · cod {p.seal.code}
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Grila de evaluare</p>
        <ol className="mt-2 grid gap-2">
          {data.criteria.map((c, i) => {
            const raw = points[String(c.n)] ?? "";
            const lv = levelOf(raw, c.max, data.levels);
            const bad = raw !== "" && problems[i];
            return (
              <li key={c.n} className="grid gap-2 rounded-xl bg-white p-3 ring-1 ring-[#E5E7EB] sm:grid-cols-[1fr_auto] sm:items-start">
                <div className="min-w-0">
                  <p><strong>{c.n}. {c.label}</strong> <span className="text-xs text-[#6B7280]">· maxim {c.max} · {c.dimension}</span></p>
                  <p className="mt-0.5 text-xs leading-relaxed text-[#6B7280]">{c.descriptor}</p>
                  {bad && <p className="mt-1 text-xs font-semibold text-[#c0483f]">{problems[i]}</p>}
                </div>
                <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                  <label className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                    <input className="w-20 rounded-lg border border-[#E5E7EB] px-2 py-1.5 text-right text-sm tabular-nums text-[#373A45] focus:border-[#965EA7] focus:outline-none disabled:bg-[#F5F6F6]"
                      inputMode="decimal" value={raw} disabled={signed} aria-label={`Punctajul la criteriul ${c.n}, din ${c.max}`}
                      onChange={(e) => setPoints({ ...points, [String(c.n)]: e.target.value })} data-testid={`ev-point-${c.n}`} />
                    <span className="w-7">/ {c.max}</span>
                  </label>
                  {lv && !bad && <Chip tone={LEVEL_TONE[lv.key]}>{lv.label}</Chip>}
                </div>
              </li>
            );
          })}
        </ol>
        <p className="mt-3 text-base font-semibold" data-testid="ev-total">Total: {total || "—"} <span className="text-sm font-normal text-[#6B7280]">din 100</span></p>
        <textarea className={`${fieldCls} mt-3`} rows={2} value={note} disabled={signed} onChange={(e) => setNote(e.target.value)}
          placeholder={`Observații (opțional; apar în coloana „Obs.” a Anexei ${ROUND_LABELS[round].individual})`} aria-label="Observații" />
        {!signed && (
          <button type="button" className={`${primaryBtn} mt-3`} disabled={busy || !total} onClick={save} data-testid="ev-save">
            {busy && <Loader2 size={14} className="animate-spin" />} Salvează punctajul
          </button>
        )}
        {p.score?.saved_at && (
          <p className="mt-2 text-xs text-[#6B7280]">Salvat la {whenLabel(p.score.saved_at)}.</p>
        )}
      </div>
    </div>
  );
}

function SignSheet({ groupId, kind, round, data, onChange }) {
  const [accept, setAccept] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const left = data.rows.filter((r) => !r.score).length;
  const signed = data.sheet.status === "signed";
  const sign = async () => {
    setBusy(true);
    try {
      onChange((await api.post(`/portal/groups/${groupId}/evaluation/sign`, { full_name: name, accept },
        { params: { session: kind, round } })).data);
      toast.success(`Fișa e semnată. Anexa ${ROUND_LABELS[round].individual} e generată.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut semna fișa."));
    } finally {
      setBusy(false);
    }
  };
  const L = ROUND_LABELS[round];
  const annex8 = data.documents.filter((d) => d.kind === `annex_${L.individual}`);
  return (
    <section aria-labelledby="ev-sign" className="rounded-2xl border border-[#965EA7]/30 bg-white p-5">
      <h2 id="ev-sign" className="flex items-center gap-2 font-display text-xl font-bold text-[#373A45]">
        <ShieldCheck size={18} className="text-[#965EA7]" /> {signed ? "Fișa semnată" : "Semnează fișa"}
      </h2>
      {signed ? (
        <p className="mt-1 text-sm text-[#373A45]">
          Semnată de {data.sheet.signature.full_name} la {whenLabel(data.sheet.signature.signed_at)}, cod{" "}
          <strong className="font-mono">{data.sheet.signature.code}</strong>. Nu se mai modifică; dacă ai greșit, scrie-i echipei.
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm text-[#6B7280]">
            Punctajele se asumă prin semnătura evaluatorului (procedura, {round === "composition" ? "art. 28 alin. 4" : "art. 30 alin. 6"}). Semnătura ta e numele scris mai
            jos, împreună cu declarația; păstrăm ora, amprenta punctajelor, adresa IP și browserul, ca dovadă. După semnare,
            fișa nu se mai modifică.
          </p>
          <p className="mt-3 rounded-xl bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45]">{data.declaration.text}</p>
          <label className="mt-3 flex items-start gap-2 text-sm text-[#373A45]">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#965EA7]"
              data-testid="ev-accept" />
            Am citit declarația și o asum.
          </label>
          <label className="mt-3 block text-sm text-[#373A45]">
            Numele tău complet
            <input className={`${fieldCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder={data.me.name}
              autoComplete="name" data-testid="ev-name" />
          </label>
          {left > 0 && <p className="mt-2 text-xs text-[#8a5d12]">Mai ai de punctat {left === 1 ? "un portofoliu" : `${left} portofolii`}.</p>}
          <button type="button" className={`${primaryBtn} mt-4`} disabled={busy || left > 0 || !accept || !name.trim()} onClick={sign}
            data-testid="ev-sign">
            {busy && <Loader2 size={16} className="animate-spin" />} Semnează fișa
          </button>
        </>
      )}
      {data.sheet.reopenings.length > 0 && (
        <ul className="mt-3 grid gap-1 text-xs text-[#6B7280]">
          {data.sheet.reopenings.map((r) => (
            <li key={r.at}>Redeschisă de {r.by?.name} la {whenLabel(r.at)}: {r.reason}</li>
          ))}
        </ul>
      )}
      {annex8.length > 0 && (
        <div className="mt-4 grid gap-1.5">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Anexa {L.individual} — catalogul tău individual</p>
          {annex8.map((d) => <DocLine key={d.id} d={d} group={data.group.name} kind={kind} />)}
        </div>
      )}
    </section>
  );
}

function DocLine({ d, group, kind }) {
  const n = d.kind.replace("annex_", "");
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={d.latest ? "font-semibold text-[#373A45]" : "text-[#6B7280]"}>{versionLine(d)}</span>
      {["pdf", "xlsx"].map((fmt) => (
        <button key={fmt} type="button" className="inline-flex items-center gap-1 font-semibold text-[#965EA7] hover:underline"
          onClick={() => downloadBlob(`/portal/evaluation-documents/${d.id}/${fmt}`, `Anexa_${n}_${asciiName(group)}_${kind}_v${d.version}.${fmt}`)}>
          <Download size={12} /> {fmt === "pdf" ? "PDF" : "Excel"}
        </button>
      ))}
    </div>
  );
}

function Progress({ data }) {
  const L = ROUND_LABELS[data.round || "composition"];
  const a9 = data.documents.filter((d) => d.kind === `annex_${L.register}`);
  return (
    <section aria-labelledby="ev-progress">
      <h2 id="ev-progress" className="font-display text-xl font-bold text-[#373A45]">Comisia</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Președinte: {data.committee.president || "—"}. Media se calculează singură când semnează toți evaluatorii;
        {data.round === "final" ? " promovează programul" : " e admis pentru evaluarea finală"} cine are cel puțin {data.threshold} de
        puncte, fără rotunjire.
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {data.progress.map((s) => {
          const st = SHEET_STATES[s.status] || SHEET_STATES.draft;
          return (
            <li key={s.name} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm ring-1 ring-[#E5E7EB]">
              {s.name} <Chip tone={st.tone}>{st.label}</Chip>
            </li>
          );
        })}
      </ul>
      {a9.length > 0 && (
        <div className="mt-3 grid gap-1.5">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Anexa {L.register} — catalogul centralizator</p>
          {a9.map((d) => <DocLine key={d.id} d={d} group={data.group.name} kind={data.session?.kind || "main"} />)}
        </div>
      )}
      {data.all_signed && <p className="mt-2 text-xs text-[#6B7280]">Mediile, cu două zecimale, sunt trecute lângă fiecare nume.</p>}
    </section>
  );
}
